// src/service/FacilityService.ts
import axiosClient from "./axiosClient";
import { IFacilityDTO } from "@interfaces/IFacilityDTO";

// Combobox-friendly shape, mirroring PlantOption ({ id, value }).
export interface FacilityOption {
  id: number;
  value: string;
}

/** Build a human-readable label for a facility option. */
export function buildFacilityLabel(facility: IFacilityDTO): string {
  const parts = [facility.type, facility.invoice_code].filter(Boolean);
  return parts.length ? parts.join(" - ") : `Facility ${facility.id ?? ""}`.trim();
}

const FacilityService = {
  getFacilitiesByPlant: async (plantId: number): Promise<IFacilityDTO[]> => {
    try {
      const response = await axiosClient.get(
        `/facility_api/facilities/plant/${plantId}`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error(`Error fetching facilities for plant ${plantId}:`, error);
      throw error;
    }
  },

  // Fetch facilities for a plant already shaped for the Combobox.
  getFacilityOptionsByPlant: async (
    plantId: number
  ): Promise<FacilityOption[]> => {
    const facilities = await FacilityService.getFacilitiesByPlant(plantId);
    return facilities
      .filter((f) => f.id != null)
      .map((f) => ({ id: f.id as number, value: buildFacilityLabel(f) }));
  },

  getFacilityById: async (id: number): Promise<IFacilityDTO | null> => {
    try {
      const response = await axiosClient.get(`/facility_api/facility/${id}`);
      return response.data || null;
    } catch (error) {
      console.error(`Error fetching facility ${id}:`, error);
      throw error;
    }
  },

  createFacility: async (data: Partial<IFacilityDTO>): Promise<IFacilityDTO> => {
    const response = await axiosClient.post("/facility_api/create", data);
    return response.data;
  },

  updateFacility: async (data: Partial<IFacilityDTO>): Promise<IFacilityDTO> => {
    const response = await axiosClient.post("/facility_api/update", data);
    return response.data;
  },

  deleteFacility: async (id: number): Promise<void> => {
    await axiosClient.delete(`/facility_api/delete/${id}`);
  },
};

export default FacilityService;
